import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TableHead, TableRow } from "@/components/ui/table";
import { Filter, Download, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

// ---- Filtrado ----
export function applyColumnFilters(rows, columns, filters) {
  return (rows || []).filter((row) =>
    columns.every((c) => {
      const val = filters[c.key];
      if (!val) return true;
      const cell = c.accessor ? c.accessor(row) : "";
      const s = String(cell ?? "").toLowerCase();
      if (c.type === "select") return s === String(val).toLowerCase();
      return s.includes(String(val).toLowerCase());
    })
  );
}

// ---- Orden ----
function cmp(a, b) {
  const na = typeof a === "number" ? a : null;
  const nb = typeof b === "number" ? b : null;
  if (na !== null && nb !== null) return na - nb;
  return String(a ?? "").localeCompare(String(b ?? ""), "es", { numeric: true, sensitivity: "base" });
}

// ---- Hook central: filtros conmutables + orden + vista resultante ----
export function useTableTools(rows, columns) {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const toggleFilters = () =>
    setShowFilters((s) => {
      if (s) setFilters({}); // al desactivar, se quitan los filtros aplicados
      return !s;
    });

  const toggleSort = (key) =>
    setSort((cur) =>
      cur.key !== key ? { key, dir: "asc" } : cur.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }
    );

  const view = useMemo(() => {
    let out = applyColumnFilters(rows, columns, filters);
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const acc = col && (col.sortAccessor || col.accessor);
      if (acc) out = [...out].sort((a, b) => cmp(acc(a), acc(b)) * (sort.dir === "asc" ? 1 : -1));
    }
    return out;
  }, [rows, columns, filters, sort]);

  return { showFilters, toggleFilters, filters, setFilters, sort, toggleSort, view };
}

// ---- Fila de encabezados con orden ----
export function SortableHeaderRow({ columns, sort, toggleSort }) {
  return (
    <TableRow>
      {columns.map((c, i) => {
        const sortable = c.type !== "none" && c.sortable !== false && !!c.label;
        const active = sort.key === c.key;
        return (
          <TableHead
            key={c.key || i}
            className={sortable ? "cursor-pointer select-none whitespace-nowrap" : ""}
            onClick={sortable ? () => toggleSort(c.key) : undefined}
            data-testid={sortable ? `sort-${c.key}` : undefined}
          >
            <span className="inline-flex items-center gap-1">
              {c.label || ""}
              {sortable && (active
                ? (sort.dir === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
                : <ChevronsUpDown className="w-3 h-3 opacity-30" />)}
            </span>
          </TableHead>
        );
      })}
    </TableRow>
  );
}

// ---- Fila de filtros (tono distinto al encabezado) ----
export function ColumnFilterRow({ columns, filters, setFilters }) {
  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <TableRow className="bg-sky-50 hover:bg-sky-50 border-y border-sky-200" data-testid="column-filter-row">
      {columns.map((c, i) => (
        <TableHead key={c.key || i} className="p-2 align-top">
          {c.type === "select" ? (
            <Select value={filters[c.key] || "__all__"} onValueChange={(v) => upd(c.key, v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs bg-sky-100 border-sky-400 focus:ring-sky-500 text-sky-900" data-testid={`filter-${c.key}`}><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {(c.options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : c.type === "none" ? null : (
            <Input value={filters[c.key] || ""} onChange={(e) => upd(c.key, e.target.value)} placeholder={c.placeholder || "Filtrar"} className="h-8 text-xs bg-sky-100 border-sky-400 focus-visible:ring-sky-500 text-sky-900 placeholder:text-sky-500/70" data-testid={`filter-${c.key}`} />
          )}
        </TableHead>
      ))}
    </TableRow>
  );
}

// ---- Exportar a Excel (respeta la vista/filtros actuales, sin dependencias) ----
function esc(v) {
  return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
export function exportToExcel(rows, columns, filename = "export") {
  const cols = columns.filter((c) => c.type !== "none" && c.label);
  const head = `<tr>${cols.map((c) => `<th style="background:#0F172A;color:#fff;text-align:left">${esc(c.label)}</th>`).join("")}</tr>`;
  const body = (rows || []).map((r) => `<tr>${cols.map((c) => {
    let v = c.accessor ? c.accessor(r) : "";
    if (c.type === "select" && c.options) { const o = c.options.find((o) => o.value === v); if (o) v = o.label; }
    return `<td>${esc(v)}</td>`;
  }).join("")}</tr>`).join("");
  const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">${head}${body}</table></body></html>`;
  const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.xls`;
  document.body.appendChild(a); a.click(); a.remove();
}

// ---- Barra de herramientas: toggle de filtros + exportar ----
export function TableToolbar({ tools, columns, filename }) {
  return (
    <div className="flex items-center gap-2">
      <Button variant={tools.showFilters ? "default" : "outline"} size="sm" onClick={tools.toggleFilters} data-testid="toggle-filters" aria-pressed={tools.showFilters}>
        <Filter className="w-4 h-4 mr-1.5" /> {tools.showFilters ? "Ocultar filtros" : "Filtros"}
      </Button>
      <Button variant="outline" size="sm" onClick={() => exportToExcel(tools.view, columns, filename)} data-testid="export-excel">
        <Download className="w-4 h-4 mr-1.5" /> Exportar a Excel
      </Button>
    </div>
  );
}
